import { Calendar, Clock, Sparkles, Video, MapPin, AlertCircle } from "lucide-react";

function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}
function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

const SESSION_TYPES = [
  { id: "one_on_one", label: "One-on-one" },
  { id: "parent_guidance", label: "Parent guidance" },
  { id: "family", label: "Family" },
];

export default function ConfirmStep({ selected, chosenSlot, sessionType, setSessionType, sessionMode, setSessionMode, topic, setTopic, submitting, onConfirm }) {
  const officeAddress = (selected.office_address || "").trim();
  const inPersonDisabled = !officeAddress;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="book-step-3">
      <div className="brand-card p-8">
        <h3 className="text-xl font-extrabold text-[#3D405B] mb-4">A bit about your session</h3>

        {/* Session mode toggle */}
        <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">How would you like to meet?</label>
        <div className="grid grid-cols-2 gap-2 mb-5">
          <button
            onClick={() => setSessionMode("online")}
            className={`p-4 rounded-2xl text-left transition-all border-2 ${sessionMode === "online" ? "border-primary bg-primary/5 shadow-sm" : "border-[#3D405B]/10 hover:border-[#3D405B]/25"}`}
            data-testid="session-mode-online"
          >
            <Video size={18} className={sessionMode === "online" ? "text-primary mb-2" : "text-[#7A7D96] mb-2"} />
            <div className="font-bold text-[#3D405B] text-sm">Online video</div>
            <div className="text-xs text-[#7A7D96] mt-0.5">Private Jitsi room link</div>
          </button>
          <button
            onClick={() => !inPersonDisabled && setSessionMode("in_person")}
            disabled={inPersonDisabled}
            className={`p-4 rounded-2xl text-left transition-all border-2 ${sessionMode === "in_person" ? "border-primary bg-primary/5 shadow-sm" : "border-[#3D405B]/10 hover:border-[#3D405B]/25"} ${inPersonDisabled ? "opacity-50 cursor-not-allowed hover:border-[#3D405B]/10" : ""}`}
            data-testid="session-mode-in_person"
          >
            <MapPin size={18} className={sessionMode === "in_person" ? "text-primary mb-2" : "text-[#7A7D96] mb-2"} />
            <div className="font-bold text-[#3D405B] text-sm">In person</div>
            <div className="text-xs text-[#7A7D96] mt-0.5">{inPersonDisabled ? "No address on file" : "At the counsellor's office"}</div>
          </button>
        </div>
        {inPersonDisabled && (
          <div className="mb-5 p-3 rounded-xl bg-accent/20 text-[#3D405B] text-xs flex items-start gap-2" data-testid="in-person-unavailable">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{selected.name.split(" ")[0]} hasn't added an office address yet. Pick online for now, or ask them to update their address.</span>
          </div>
        )}
        {sessionMode === "in_person" && !inPersonDisabled && (
          <div className="mb-5 p-3 rounded-xl bg-secondary/10 border border-secondary/20 text-[#3D405B] text-sm flex items-start gap-2" data-testid="in-person-address-preview">
            <MapPin size={14} className="text-secondary mt-0.5 shrink-0" />
            <span><strong>Office address:</strong> {officeAddress}</span>
          </div>
        )}

        <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Session type</label>
        <div className="flex flex-wrap gap-2 mb-5">
          {SESSION_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setSessionType(t.id)}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-all ${sessionType === t.id ? "bg-secondary text-white" : "bg-muted text-[#3D405B] hover:bg-[#3D405B]/10"}`}
              data-testid={`session-type-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">What's on your mind? (optional)</label>
        <textarea
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          rows={4}
          placeholder="A short note your counsellor can read before the session."
          className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none resize-none"
          data-testid="book-topic-input"
        />
      </div>

      <div className="brand-card p-8 bg-gradient-to-br from-secondary/10 to-accent/15">
        <Sparkles className="text-primary mb-3" size={22} />
        <h3 className="text-xl font-extrabold text-[#3D405B] mb-4">You're booking with</h3>
        <div className="flex items-center gap-3 mb-5">
          {selected.picture ? <img src={selected.picture} className="w-14 h-14 rounded-2xl object-cover" alt="" /> : null}
          <div>
            <div className="font-extrabold text-[#3D405B]">{selected.name}</div>
            <div className="text-xs text-[#7A7D96]">Registered counsellor</div>
          </div>
        </div>
        <div className="text-sm text-[#3D405B] space-y-2 mb-6">
          <div className="flex items-center gap-2"><Calendar size={15} className="text-primary" /> {fmtDate(chosenSlot.start_at)}</div>
          <div className="flex items-center gap-2"><Clock size={15} className="text-primary" /> {fmtTime(chosenSlot.start_at)} · 50 minutes</div>
          <div className="flex items-center gap-2">
            {sessionMode === "online" ? <Video size={15} className="text-primary" /> : <MapPin size={15} className="text-primary" />}
            {sessionMode === "online" ? "Online video session" : "In-person session"}
          </div>
        </div>
        <button
          onClick={onConfirm}
          disabled={submitting}
          className="w-full px-7 py-3.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60"
          data-testid="book-confirm-btn"
        >
          {submitting ? "Booking…" : "Confirm booking"}
        </button>
        <p className="text-xs text-[#7A7D96] text-center mt-3">You can cancel any time before the session.</p>
      </div>
    </div>
  );
}
